import { useMemo } from 'react';
import { Collapse, Empty, List, Space } from 'antd';

import Markdown from '../Markdown';
import styles from './index.less';
import { FlowModuleItemType } from '@packages/flow-core/module/types';

export type GeneratingStatus = 'running' | 'done';

type StreamResponseDataType = {
  status: GeneratingStatus;
  moduleInfo: FlowModuleItemType & {
    outputMap: Record<string, any>;
    inputMap: Record<string, any>;
  };
  outerParentId?: string[]; // 外部父节点id，不存在时为一级调用，否则为多层嵌套
  runTime: number;
  dialog?: boolean;
};

type Props = {
  dataSource: StreamResponseDataType[];
};

interface IRenderTree extends StreamResponseDataType {
  children: IRenderTree[];
}

export const findRealPointer = (result: IRenderTree[], ids: string[]) => {
  let pointer: IRenderTree | undefined = void 0;

  for (let i = 0; i < ids.length; i++) {
    if (i === 0) {
      pointer = result.find((r) => r.moduleInfo.moduleId === ids[0]);
      continue;
    }
    pointer = pointer?.children.find((p) => p.moduleInfo.moduleId === ids[i]);
  }

  return pointer;
};

/**
 * 重整化排序日志输出，树形结构渲染
 * @param dataSource
 */
const reformatDataSource = (dataSource: StreamResponseDataType[]): IRenderTree[] => {
  const result: IRenderTree[] = [];
  const handledIds: string[] = [];

  dataSource.forEach((data) => {
    const id = data.moduleInfo.moduleId;
    const pointerId = [...(data.outerParentId ?? []), id].join('-');
    if (handledIds.includes(pointerId)) {
      const pointer = findRealPointer(result, [...(data.outerParentId ?? []), id]);
      if (pointer) {
        pointer.runTime = data.runTime;
      }
    } else {
      if (!data.outerParentId || data.outerParentId.length === 0) {
        result.push({ ...data, children: [] });
        handledIds.push(id);
      } else {
        let tmp_pointer: IRenderTree | undefined = findRealPointer(result, data.outerParentId);
        tmp_pointer?.children.push({ ...data, children: [] });
        handledIds.push(`${data.outerParentId.join('-')}-${id}`);
      }
    }
  });

  return result;
};

/**
 * 递归渲染生成 tree
 */
const recursiveRenderingComponent = (tree: IRenderTree) => {
  if (tree.children.length > 0) {
    return (
      <List.Item>
        <Collapse
          className={styles.collapse}
          items={[
            {
              key: '1',
              label: (
                <Space>
                  <img src={tree.moduleInfo.avatar} className={styles.avatar} />
                  <strong>{tree.moduleInfo.name}</strong>
                  <span>运行时间：{tree.runTime / 1000}s</span>
                </Space>
              ),
              headerClass: styles.header,
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={tree.children}
                  locale={{ emptyText: <Empty description="暂无调试信息" /> }}
                  renderItem={(tree) => recursiveRenderingComponent(tree)}
                />
              ),
            },
          ]}
        />
      </List.Item>
    );
  }

  return (
    <List.Item>
      <Collapse
        className={styles.collapse}
        items={[
          {
            key: '1',
            label: (
              <Space>
                <img src={tree.moduleInfo.avatar} className={styles.avatar} />
                <strong>{tree.moduleInfo.name}</strong>
                <span>运行时间：{tree.runTime / 1000}s</span>
              </Space>
            ),
            headerClass: styles.header,
            children: (
              <>
                <div>模块输入值：</div>
                <Markdown
                  source={'```json\n' + JSON.stringify(tree.moduleInfo.inputMap, null, 2) + '\n```'}
                />
                <div>模块输出值：</div>
                <Markdown
                  source={
                    '```json\n' + JSON.stringify(tree.moduleInfo.outputMap, null, 2) + '\n```'
                  }
                />
              </>
            ),
          },
        ]}
      ></Collapse>
    </List.Item>
  );
};

export default function WorkflowDebugList({ dataSource }: Props) {
  const renderTree = useMemo(
    () => reformatDataSource(dataSource.filter((d) => !d.dialog)),
    [dataSource],
  );

  return (
    <List
      itemLayout="vertical"
      dataSource={renderTree}
      locale={{ emptyText: <Empty description="暂无调试信息" /> }}
      renderItem={(tree) => recursiveRenderingComponent(tree)}
    />
  );
}
